// frontend/src/components/PrescriptionTab.js

import React, { forwardRef, useImperativeHandle, useCallback } from "react";
import { PlusIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { MedicationRow, FieldLabel, emptyMedication } from "./MedicationRow";
import { usePrescription } from "./PrescriptionContext";

const PrescriptionTab = forwardRef(({ 
  onDirtyChange, 
  onSummaryChange,
}, ref) => {
  const {
    medicationsList,
    setMedicationsList,
    notes,
    isGenerating,
    generateError,
    canGenerate,
    generatePrescription,
    recommendations, // Get recommendations from context
  } = usePrescription();

  useImperativeHandle(ref, () => ({
    getData: () => ({
      medications: medicationsList.filter((m) => m.drugName?.trim() || m.product_id),
      notes,
    }),
    resetDirty: () => {},
  }));

  const addMedication = () => {
    setMedicationsList((prev) => [...prev, emptyMedication()]);
    onDirtyChange?.(true);
  };

  const updateMedication = (idx, val) => {
    setMedicationsList((prev) => {
      const updated = [...prev];
      updated[idx] = val;
      return updated;
    });
    onDirtyChange?.(true);
  };

  const removeMedication = (idx) => {
    setMedicationsList((prev) => prev.filter((_, i) => i !== idx));
    onDirtyChange?.(true);
  };

  // Handle generate prescription - use recommendations from context
  const handleGeneratePrescription = useCallback(async () => {
    console.log('📋 Current recommendations from context:', recommendations);
    // The generatePrescription function will use recommendations from context state
    await generatePrescription();
  }, [generatePrescription, recommendations]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <FieldLabel>Medications</FieldLabel>

        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleGeneratePrescription}
            disabled={isGenerating || !canGenerate}
            className="flex items-center gap-1.5 text-[12px] font-bold text-slate-600 hover:text-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowDownTrayIcon className={`w-4 h-4 ${isGenerating ? "animate-pulse" : ""}`} />
            {isGenerating ? "Preparing..." : "Generate Prescription"}
          </button>

          <button
            type="button"
            onClick={addMedication}
            className="flex items-center gap-1.5 text-[12px] font-bold text-emerald-600 hover:text-emerald-700"
          >
            <PlusIcon className="w-4 h-4" /> Add Medication
          </button>
        </div>
      </div>

      {generateError && (
        <p className="text-[11px] text-red-500 mb-2">{generateError}</p>
      )}

      {medicationsList.length === 0 ? (
        <button
          type="button"
          onClick={addMedication}
          className="w-full border-2 border-dashed border-slate-200 rounded-xl py-8 text-center hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors"
        >
          <p className="text-[12px] text-slate-400">No medications prescribed yet — click to add one</p>
        </button>
      ) : (
        <div className="space-y-3">
          {medicationsList.map((item, idx) => (
            <MedicationRow
              key={item.id || idx}
              item={item}
              onChange={(val) => updateMedication(idx, val)}
              onRemove={() => removeMedication(idx)}
            />
          ))}
        </div>
      )}
    </div>
  );
});

PrescriptionTab.displayName = "PrescriptionTab";

export default PrescriptionTab;